// @ts-nocheck
import type { Question } from '../types';

export const adasSafetyQuestions: Question[] = [
  // --- ISA (Intelligent Speed Assistance) ---
  {
    id: 'q-adas-1',
    topic: 'General',
    question: 'Your car has Intelligent Speed Assistance (ISA). You are driving on a road where the speed limit has recently changed. What should you do?',
    options: [
      { id: 'q-adas-1o1', text: 'Trust ISA completely — it reads all speed signs automatically' },
      { id: 'q-adas-1o2', text: 'Stay alert and check actual signs — ISA can misread temporary or faded signs' },
      { id: 'q-adas-1o3', text: 'Turn off ISA when you see a new speed sign' },
    ],
    answerId: 'q-adas-1o2',
    explanation: 'ISA uses cameras and GPS to read speed limits, but it can misread temporary signs, roadworks limits, or faded markings. You must always check the actual signs and remain responsible for your speed.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },
  {
    id: 'q-adas-2',
    topic: 'General',
    question: 'ISA in your vehicle is warning you that you are exceeding the speed limit, but you believe the limit on this road is higher. What is the correct action?',
    options: [
      { id: 'q-adas-2o1', text: 'Override ISA and trust your own judgement, but verify the limit on the next sign' },
      { id: 'q-adas-2o2', text: 'Always follow ISA — it is legally binding' },
      { id: 'q-adas-2o3', text: 'Switch ISA off permanently so it stops intervening' },
    ],
    answerId: 'q-adas-2o1',
    explanation: 'ISA is a driver assistance tool, not a legal authority. You can override it, but you must verify the correct speed limit from road signs. You remain responsible for complying with the actual limit.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },

  // --- AEB (Autonomous Emergency Braking) ---
  {
    id: 'q-adas-3',
    topic: 'General',
    question: 'You are driving at 80 km/h in heavy rain. Your car has Autonomous Emergency Braking (AEB). A car brakes suddenly ahead. What should you do?',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/adas-safety/adas-1-aeb-heavy-rain-braking.jpg',
    options: [
      { id: 'q-adas-3o1', text: 'Rely on AEB to stop the car — it works in all conditions' },
      { id: 'q-adas-3o2', text: 'Brake immediately yourself — AEB performance is reduced in heavy rain' },
      { id: 'q-adas-3o3', text: 'Steer around the car and let AEB handle braking' },
    ],
    answerId: 'q-adas-3o2',
    explanation: 'AEB uses cameras and radar that can be impaired by heavy rain, snow, or dirt on sensors. Always maintain a safe following distance and brake yourself — do not rely solely on AEB to prevent a collision.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },
  {
    id: 'q-adas-4',
    topic: 'General',
    question: 'AEB (Autonomous Emergency Braking) activates unexpectedly on an open road with no obstacle. What is the most likely cause?',
    options: [
      { id: 'q-adas-4o1', text: 'The system is malfunctioning and you should have it checked' },
      { id: 'q-adas-4o2', text: 'A dirty or obscured sensor is triggering a false reading' },
      { id: 'q-adas-4o3', text: 'Both A and B are possible — have the sensors cleaned and the system inspected' },
    ],
    answerId: 'q-adas-4o3',
    explanation: 'False AEB activations can be caused by dirty sensors, sun glare, overhanging branches, or a system fault. Always clean sensors regularly and have unexpected activations inspected by a mechanic.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },

  // --- ACC (Adaptive Cruise Control) ---
  {
    id: 'q-adas-5',
    topic: 'General',
    question: 'You are using Adaptive Cruise Control (ACC) on a motorway. Traffic suddenly stops ahead due to a collision. What must you do?',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/adas-safety/adas-6-acc-safe-distance.jpg',
    options: [
      { id: 'q-adas-5o1', text: 'Nothing — ACC will bring the car to a full stop automatically in all situations' },
      { id: 'q-adas-5o2', text: 'Take over immediately — ACC may not detect a full stop quickly enough at high speed' },
      { id: 'q-adas-5o3', text: 'Sound your horn to warn other drivers while ACC handles braking' },
    ],
    answerId: 'q-adas-5o2',
    explanation: 'ACC maintains a set following distance but has limitations, especially at high speed or in sudden stop situations. You must always be prepared to take over immediately. ACC does not replace attentive driving.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },
  {
    id: 'q-adas-6',
    topic: 'General',
    question: 'You are driving with ACC set to 120 km/h on a wet motorway. Why should you manually reduce speed?',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/adas-safety/adas-7-acc-wet-road.jpg',
    options: [
      { id: 'q-adas-6o1', text: 'ACC automatically adjusts speed for road conditions' },
      { id: 'q-adas-6o2', text: 'ACC maintains your set speed regardless of road conditions — you must reduce it manually' },
      { id: 'q-adas-6o3', text: 'ACC is not allowed on wet roads under Dutch law' },
    ],
    answerId: 'q-adas-6o2',
    explanation: 'ACC does not detect road surface conditions such as rain, ice, or aquaplaning risk. It maintains the set speed unless it detects a slower vehicle ahead. You are responsible for setting an appropriate speed for conditions.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },

  // --- Lane Keeping Assist / LDW ---
  {
    id: 'q-adas-7',
    topic: 'General',
    question: 'Lane Keeping Assist (LKA) stops working and a warning light appears. You are on a motorway. What should you do?',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/adas-safety/adas-3-lka-clear-markings.jpg',
    options: [
      { id: 'q-adas-7o1', text: 'Pull over immediately — driving without LKA is illegal' },
      { id: 'q-adas-7o2', text: 'Continue driving attentively without relying on LKA and have it checked soon' },
      { id: 'q-adas-7o3', text: 'Increase speed so you rely on LKA less' },
    ],
    answerId: 'q-adas-7o2',
    explanation: 'LKA is a driving aid, not a legal requirement. If it fails, continue driving with full manual attention and have the system checked at the next opportunity. You must always be capable of driving safely without driver assistance systems.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },
  {
    id: 'q-adas-8',
    topic: 'General',
    question: 'You are driving in heavy snow. Your Lane Departure Warning (LDW) system has switched off automatically. Why?',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/adas-safety/adas-4-lka-obscured-snow.jpg',
    options: [
      { id: 'q-adas-8o1', text: 'Snow has covered the lane markings the camera uses to detect lane position' },
      { id: 'q-adas-8o2', text: 'Cold weather causes the system to shut down to save battery' },
      { id: 'q-adas-8o3', text: 'Dutch law requires LDW to be off in winter conditions' },
    ],
    answerId: 'q-adas-8o1',
    explanation: 'LDW and LKA rely on cameras to detect lane markings. Snow, mud, heavy rain, or faded road markings can make lane detection impossible, causing the system to deactivate. Increase your own attention in these conditions.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },

  // --- Blind Spot Monitoring ---
  {
    id: 'q-adas-9',
    topic: 'General',
    question: 'Your Blind Spot Monitoring (BSM) system shows no warning when you want to change lanes. What must you still do?',
    options: [
      { id: 'q-adas-9o1', text: 'Change lanes — BSM has confirmed the lane is clear' },
      { id: 'q-adas-9o2', text: 'Check your mirrors and look over your shoulder before changing lanes' },
      { id: 'q-adas-9o3', text: 'Sound your horn to warn other road users first' },
    ],
    answerId: 'q-adas-9o2',
    explanation: 'BSM assists but cannot detect all situations — fast-approaching vehicles, cyclists, or motorcycles may not trigger the warning in time. Always check mirrors and your blind spot physically before every lane change.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },
  {
    id: 'q-adas-10',
    topic: 'General',
    question: 'A cyclist is in your right blind spot. Your BSM warning light is showing. What should you do?',
    options: [
      { id: 'q-adas-10o1', text: 'Wait for the BSM warning to disappear before turning right' },
      { id: 'q-adas-10o2', text: 'Look over your shoulder to confirm before proceeding — BSM may not detect cyclists reliably' },
      { id: 'q-adas-10o3', text: 'Tap your brakes to warn the cyclist, then turn' },
    ],
    answerId: 'q-adas-10o2',
    explanation: 'BSM warnings are helpful but not definitive. Some systems may not reliably detect cyclists or motorcycles. Always physically check your blind spot, especially when turning or changing lanes near vulnerable road users.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },

  // --- TPMS (Tire Pressure Monitoring) ---
  {
    id: 'q-adas-13',
    topic: 'General',
    question: 'The TPMS (Tire Pressure Monitoring System) warning light comes on while driving. What should you do?',
    options: [
      { id: 'q-adas-13o1', text: 'Continue driving — the system monitors itself and will alert you if pressure becomes critical' },
      { id: 'q-adas-13o2', text: 'Reduce speed, check tire pressure at the earliest opportunity, and do not drive long distances on a significantly under-inflated tire' },
      { id: 'q-adas-13o3', text: 'Inflate all four tires at the next fuel station, regardless of current pressure' },
    ],
    answerId: 'q-adas-13o2',
    explanation: 'A TPMS warning indicates at least one tire is significantly under-inflated, which affects braking, handling, and increases blowout risk. Reduce speed and check pressures promptly. Driving long distances on a flat can destroy the tire and rim.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },

  // --- Driver Drowsiness Detection ---
  {
    id: 'q-adas-14',
    topic: 'General',
    question: 'Your car\'s drowsiness detection system gives a coffee cup warning. What does this mean and what should you do?',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/adas-safety/adas-8-drowsiness-alert.jpg',
    options: [
      { id: 'q-adas-14o1', text: 'The system has detected driving patterns that suggest fatigue — stop at the nearest rest area and rest' },
      { id: 'q-adas-14o2', text: 'You need to refuel — the icon resembles a fuel pump' },
      { id: 'q-adas-14o3', text: 'You are driving too fast — reduce speed immediately' },
    ],
    answerId: 'q-adas-14o1',
    explanation: 'A coffee cup icon from the drowsiness detection system indicates the system has detected steering patterns or driving behaviour associated with fatigue. Stop safely and rest. Fatigue is a major cause of serious accidents.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },
  {
    id: 'q-adas-15',
    topic: 'General',
    question: 'You feel fine but the drowsiness alert keeps triggering. What is the safest response?',
    options: [
      { id: 'q-adas-15o1', text: 'Ignore it — the system is faulty if you do not feel tired' },
      { id: 'q-adas-15o2', text: 'Take a break anyway — the system can detect micro-fatigue before you are consciously aware of it' },
      { id: 'q-adas-15o3', text: 'Turn on the radio to stay alert and dismiss the warning' },
    ],
    answerId: 'q-adas-15o2',
    explanation: 'Fatigue impairs judgement about your own alertness. The drowsiness system analyses driving patterns and can detect reduced performance before you feel tired. When in doubt, stop and rest — a 15-minute break significantly reduces fatigue.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },

  // --- Traffic Sign Recognition ---
  {
    id: 'q-adas-16',
    topic: 'General',
    question: 'Your car\'s Traffic Sign Recognition (TSR) displays a 30 km/h limit, but you can see a 50 km/h sign on the road. Which limit applies?',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/adas-safety/adas-10-tsr-misread.jpg',
    options: [
      { id: 'q-adas-16o1', text: 'The TSR display — it reads signs more accurately than human eyes' },
      { id: 'q-adas-16o2', text: 'The actual road sign — TSR can misread signs and you must always follow the physical sign' },
      { id: 'q-adas-16o3', text: 'The lower limit — always choose the safer option when systems disagree' },
    ],
    answerId: 'q-adas-16o2',
    explanation: 'TSR cameras can misread signs due to sun glare, dirt, angle, or sign damage. The physical road sign always takes legal precedence. You are responsible for obeying actual traffic signs, not your car\'s display.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },

  // --- Parking Sensors and Camera ---
  {
    id: 'q-adas-17',
    topic: 'General',
    question: 'You are reversing using only your 360° camera system. The system shows no obstacles. Is it safe to reverse?',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/adas-safety/adas-11-parking-camera.jpg',
    options: [
      { id: 'q-adas-17o1', text: 'Yes — 360° cameras show everything around the vehicle' },
      { id: 'q-adas-17o2', text: 'Not necessarily — cameras have blind spots, lag, and may not detect low or fast-moving objects' },
      { id: 'q-adas-17o3', text: 'Only if your parking sensors also confirm it is clear' },
    ],
    answerId: 'q-adas-17o2',
    explanation: 'Camera and sensor systems have limitations — blind spots, poor performance in bright sunlight, slow refresh rates, and difficulty detecting small children or animals. Always physically check before reversing and move slowly.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },

  // --- Driver Responsibility ---
  {
    id: 'q-adas-18',
    topic: 'General',
    question: 'Your vehicle has Level 2 driver assistance (ACC + LKA active). You take your hands off the wheel and look at your phone. What is the legal position?',
    options: [
      { id: 'q-adas-18o1', text: 'You are not responsible — the system is driving the vehicle' },
      { id: 'q-adas-18o2', text: 'You are still fully responsible — Level 2 requires constant driver supervision and hands on the wheel' },
      { id: 'q-adas-18o3', text: 'You are only partially responsible if the system causes an accident' },
    ],
    answerId: 'q-adas-18o2',
    explanation: 'Level 2 automation (ACC + LKA) requires the driver to remain in control at all times with hands on the wheel. The driver is fully legally responsible. Using a phone while driving is illegal regardless of which assistance systems are active.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },
  {
    id: 'q-adas-19',
    topic: 'General',
    question: 'A road user is injured in an accident where your ACC system failed to brake in time. Who is liable?',
    options: [
      { id: 'q-adas-19o1', text: 'The car manufacturer, since the ADAS system failed' },
      { id: 'q-adas-19o2', text: 'You as the driver — you remain responsible for maintaining a safe following distance at all times' },
      { id: 'q-adas-19o3', text: 'Neither — ADAS failures are classified as unavoidable accidents' },
    ],
    answerId: 'q-adas-19o2',
    explanation: 'Driver assistance systems do not transfer legal responsibility to the manufacturer. As the driver, you are always responsible for safe vehicle control. Maintain your own safe following distance and do not rely solely on ACC.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },
  {
    id: 'q-adas-20',
    topic: 'General',
    question: 'When must you always be capable of taking full manual control of your vehicle, regardless of which ADAS systems are active?',
    options: [
      { id: 'q-adas-20o1', text: 'Only when ADAS systems give a warning signal' },
      { id: 'q-adas-20o2', text: 'At all times — driver assistance systems can fail at any moment without warning' },
      { id: 'q-adas-20o3', text: 'Only at speeds above 100 km/h where risks are higher' },
    ],
    answerId: 'q-adas-20o2',
    explanation: 'ADAS systems can fail without warning due to sensor degradation, software errors, weather, or unexpected road situations. You must always be alert and ready to take full manual control immediately. This is a fundamental principle of driving with ADAS.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },

  // --- ABS practical ---
  {
    id: 'q-adas-21',
    topic: 'General',
    question: 'You need to brake hard in an emergency on a road with ABS. What is the correct technique?',
    options: [
      { id: 'q-adas-21o1', text: 'Pump the brakes rapidly to prevent wheel lock' },
      { id: 'q-adas-21o2', text: 'Press the brake pedal firmly and hold — ABS prevents wheel lock and you can steer' },
      { id: 'q-adas-21o3', text: 'Brake gently to avoid triggering ABS, which can increase stopping distance' },
    ],
    answerId: 'q-adas-21o2',
    explanation: 'With ABS, press the brake pedal hard and keep it pressed. The system automatically pulses the brakes to prevent wheel lock. Unlike pre-ABS driving, you do not need to pump the brake. The vibration you feel through the pedal is normal — keep pressing.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },

  // --- 2026 EU mandatory systems ---
  {
    id: 'q-adas-24',
    topic: 'General',
    question: 'Since July 2024, which safety systems are mandatory on all new passenger cars sold in the EU?',
    options: [
      { id: 'q-adas-24o1', text: 'Autonomous driving capability and self-parking' },
      { id: 'q-adas-24o2', text: 'ISA, AEB, LDW, drowsiness detection, reversing detection, and eCall among others' },
      { id: 'q-adas-24o3', text: 'Only ABS and ESP — newer systems remain optional' },
    ],
    answerId: 'q-adas-24o2',
    explanation: 'EU Regulation 2019/2144 mandated that from July 2024, all new car types must include: ISA, AEB (for pedestrians and cyclists), LDW, drowsiness and attention detection, reversing detection, event data recorder, and eCall. ABS and ESC were already mandatory earlier.',
    subject: 'ADAS & Safety Systems',
    animationId: null,
  },
];
